import React from 'react';
import { Grid, Typography } from '@mui/material';
import moment from 'moment';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { DAY_KEYS } from './bulkSessionsUtils';

type CalendarMonthProps = {
  year: number;
  month: number;
  availableDates: Set<string>;
  selectedDates: Set<string>;
  disabledDates: Set<string>;
  onToggleDate: (date: string) => void;
  rangeStart: moment.Moment;
  rangeEnd: moment.Moment;
};

export const CalendarMonth = ({
  year,
  month,
  availableDates,
  selectedDates,
  disabledDates,
  onToggleDate,
  rangeStart,
  rangeEnd,
}: CalendarMonthProps) => {
  const language = useLanguage();
  const firstDay = moment({ year, month, day: 1 });
  const daysInMonth = firstDay.daysInMonth();
  const startDayOfWeek = firstDay.day();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Grid container flexDirection="column" sx={{ width: '100%' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', mb: 0.5 }}>
        {firstDay.format('MMMM YYYY')}
      </Typography>
      <Grid container>
        {DAY_KEYS.map((key, i) => (
          <Grid item key={key} sx={{ width: '14.28%', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.6rem' }}>
              {moment().day(i).locale(language).format('dd')}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Grid container>
        {cells.map((day, i) => {
          if (day === null) return <Grid item key={`e-${i}`} sx={{ width: '14.28%', height: 24 }} />;
          const date = moment({ year, month, day });
          const dateKey = date.format('YYYY-MM-DD');
          const isInRange = date.isSameOrAfter(rangeStart, 'day') && date.isSameOrBefore(rangeEnd, 'day');
          const isAvailable = availableDates.has(dateKey) && isInRange;
          const isSelected = selectedDates.has(dateKey);
          const isDisabled = disabledDates.has(dateKey);
          const clickable = isAvailable && !isDisabled;

          return (
            <Grid
              item
              key={day}
              sx={{
                width: '14.28%',
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: clickable || isSelected ? 'pointer' : 'default',
              }}
              onClick={() => (isSelected || clickable) && onToggleDate(dateKey)}>
              <Grid
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.65rem',
                  ...(isAvailable && isSelected
                    ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
                    : isAvailable && !isDisabled
                    ? { border: '1.5px solid', borderColor: 'primary.main', color: 'primary.main' }
                    : isAvailable && isDisabled
                    ? { color: 'text.disabled', border: '1px dashed', borderColor: 'mb.control' }
                    : { color: 'text.disabled' }),
                }}>
                {day}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};
